import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import { useCancelPlan } from 'features/subscription/hooks/use-seats';
import { PlanSeats } from 'features/subscription/utils/seat-pools';
import { primaryButtonClass } from 'features/subscription/constants/button-styles';

// Dark mode's --destructive is too dim to read as text, so use a lighter red there.
const cancelLightClass = 'border-destructive/40 !text-destructive hover:bg-destructive/10';
const cancelDarkClass = 'border-red-400/40 bg-transparent !text-red-400 hover:bg-red-400/10';

interface PendingCheckoutDialogProps {
  entry: PlanSeats;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The choice for a plan waiting for its first payment: go back to the payment page, or drop the plan. */
export const PendingCheckoutDialog = ({ entry, open, onOpenChange }: PendingCheckoutDialogProps) => {
  const { t } = useTranslation();
  const { mutateAsync: cancel, isPending: isCancelling } = useCancelPlan();
  const isDark = useTheme().resolvedTheme === 'dark';
  const pool = entry.pool;
  if (!pool) return null;

  const handleCancel = async () => {
    await cancel({ pool, now: true }).catch(() => undefined);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isCancelling && onOpenChange(isOpen)}>
      <DialogContent nested className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{t('PAYMENT_PENDING_TITLE', { plan: entry.name, defaultValue: '{{plan}} payment pending' })}</DialogTitle>
          <DialogDescription>
            {t(
              'PENDING_CHECKOUT_DESCRIPTION',
              'The first payment was never finished. Continue to the payment page, or cancel the plan. Cancelling charges nothing.'
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            className={cn('relative rounded-full px-6', isDark ? cancelDarkClass : cancelLightClass)}
            onClick={handleCancel}
            disabled={isCancelling}
          >
            {/* The spinner sits over the hidden label so the button keeps its width; a wider
                button overflows this narrow dialog and shows a horizontal scrollbar. */}
            <span className={cn(isCancelling && 'invisible')}>{t('CANCEL_CHECKOUT', 'Cancel checkout')}</span>
            {isCancelling && <Loader2 className="absolute h-4 w-4 animate-spin" />}
          </Button>
          {/* usePlanSeats cancels a plan whose payment page expired, so the link is always here. */}
          <Button
            className={cn('rounded-full px-6', primaryButtonClass(isDark))}
            onClick={() => (window.location.href = entry.checkoutUrl as string)}
            disabled={isCancelling}
          >
            {t('CONTINUE_CHECKOUT', 'Continue checkout')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Stands in for a plan card's main button while the plan waits for its first payment. One click
 * opens a choice: go back to the payment page, or drop the unpaid plan. `className` resizes the button.
 */
export const PendingCheckoutAction = ({ entry, className }: { entry: PlanSeats; className?: string }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const isDark = useTheme().resolvedTheme === 'dark';
  if (!entry.pool) return null;

  return (
    <>
      <Button
        className={cn('h-12 w-full rounded-full text-base font-semibold', primaryButtonClass(isDark), className)}
        onClick={() => setIsOpen(true)}
      >
        {t('CONTINUE_CHECKOUT', 'Continue checkout')}
      </Button>
      <PendingCheckoutDialog entry={entry} open={isOpen} onOpenChange={setIsOpen} />
    </>
  );
};
